import React, { useState } from 'react';

const OptinBadge = ({ active, label, color }) => (
  <div style={{
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '3px 8px',
    background: active ? `rgba(${color},0.12)` : 'rgba(255,255,255,0.04)',
    border: `1px solid ${active ? `rgba(${color},0.4)` : 'rgba(255,255,255,0.1)'}`,
    marginRight: '4px', marginBottom: '4px'
  }}>
    <div style={{
      width: '5px', height: '5px',
      borderRadius: '50%',
      background: active ? `rgb(${color})` : 'rgba(255,255,255,0.2)'
    }} />
    <span style={{
      fontFamily: 'monospace', fontSize: '0.5rem',
      letterSpacing: '0.12em', color: active ? `rgb(${color})` : 'rgba(238,240,242,0.3)',
      textTransform: 'uppercase'
    }}>{label}</span>
  </div>
);

const SubmissionRow = ({ submission, isExpanded, onToggle, onMarkRead, onMarkUnread, onReply, replyLoading }) => {
  const [replyText, setReplyText] = useState('');
  const [replySent, setReplySent] = useState(false);

  const msgPreview = submission?.message?.length > 50
    ? submission?.message?.substring(0, 50) + '...'
    : submission?.message;

  const timeStr = submission?.created_at
    ? new Date(submission.created_at)?.toLocaleString('nb-NO', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    : '—';

  const handleSendReply = async () => {
    if (!replyText?.trim()) return;
    await onReply(submission?.id, replyText, submission?.email);
    setReplySent(true);
    setReplyText('');
    setTimeout(() => setReplySent(false), 3000);
  };

  return (
    <>
      {/* Main row */}
      <tr
        onClick={onToggle}
        style={{
          background: isExpanded
            ? 'rgba(59,111,212,0.08)'
            : submission?.is_read
              ? 'transparent' :'rgba(232,148,26,0.04)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          cursor: 'pointer',
          transition: 'background 0.2s'
        }}
        onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
        onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = submission?.is_read ? 'transparent' : 'rgba(232,148,26,0.04)'; }}
      >
        {/* Unread indicator */}
        <td style={{ padding: '16px 8px 16px 20px', width: '24px' }}>
          {!submission?.is_read && (
            <div style={{
              width: '6px', height: '6px',
              borderRadius: '50%',
              background: '#E8941A',
              boxShadow: '0 0 6px rgba(232,148,26,0.6)'
            }} />
          )}
        </td>

        {/* Name */}
        <td style={{ padding: '16px 12px' }}>
          <div style={{
            fontFamily: '"Arial Black", sans-serif',
            fontSize: '0.8rem', fontWeight: 900,
            color: submission?.is_read ? 'rgba(238,240,242,0.7)' : '#EEF0F2',
            letterSpacing: '-0.02em', textTransform: 'uppercase'
          }}>{submission?.name}</div>
        </td>

        {/* Email */}
        <td style={{ padding: '16px 12px' }}>
          <div style={{
            fontFamily: 'monospace', fontSize: '0.68rem',
            color: '#3B6FD4', letterSpacing: '0.02em'
          }}>{submission?.email}</div>
          {submission?.phone && (
            <div style={{
              fontFamily: 'monospace', fontSize: '0.58rem',
              color: 'rgba(238,240,242,0.35)', marginTop: '2px'
            }}>{submission?.phone}</div>
          )}
        </td>

        {/* Message preview */}
        <td style={{ padding: '16px 12px', maxWidth: '220px' }}>
          <div style={{
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            fontSize: '0.72rem', color: 'rgba(238,240,242,0.55)',
            lineHeight: 1.5, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}>{msgPreview}</div>
        </td>

        {/* Opt-in status */}
        <td style={{ padding: '16px 12px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
            <OptinBadge active={submission?.optin_email} label="E-POST" color="59,111,212" />
            <OptinBadge active={submission?.optin_sms} label="SMS" color="232,148,26" />
          </div>
        </td>

        {/* Timestamp */}
        <td style={{ padding: '16px 12px' }}>
          <div style={{
            fontFamily: 'monospace', fontSize: '0.58rem',
            color: 'rgba(238,240,242,0.4)', letterSpacing: '0.05em'
          }}>{timeStr}</div>
        </td>

        {/* Read/Unread badge */}
        <td style={{ padding: '16px 12px' }}>
          <div style={{
            display: 'inline-block',
            padding: '3px 10px',
            background: submission?.is_read ? 'rgba(255,255,255,0.06)' : 'rgba(232,148,26,0.15)',
            border: `1px solid ${submission?.is_read ? 'rgba(255,255,255,0.1)' : 'rgba(232,148,26,0.4)'}`,
            fontFamily: 'monospace', fontSize: '0.48rem',
            letterSpacing: '0.2em',
            color: submission?.is_read ? 'rgba(238,240,242,0.35)' : '#E8941A',
            textTransform: 'uppercase'
          }}>
            {submission?.is_read ? 'LEST' : 'ULEST'}
          </div>
        </td>

        {/* Actions */}
        <td style={{ padding: '16px 20px 16px 12px' }}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button
              onClick={e => { e?.stopPropagation(); submission?.is_read ? onMarkUnread(submission?.id) : onMarkRead(submission?.id); }}
              title={submission?.is_read ? 'Merk som ulest' : 'Merk som lest'}
              style={{
                background: 'rgba(59,111,212,0.1)',
                border: '1px solid rgba(59,111,212,0.3)',
                color: '#3B6FD4',
                padding: '5px 8px',
                cursor: 'pointer',
                fontFamily: 'monospace', fontSize: '0.55rem',
                letterSpacing: '0.1em',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,111,212,0.25)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59,111,212,0.1)'; }}
            >
              {submission?.is_read ? '◎' : '●'}
            </button>
            <button
              onClick={e => { e?.stopPropagation(); onToggle(); }}
              title="Svar"
              style={{
                background: 'rgba(232,148,26,0.1)',
                border: '1px solid rgba(232,148,26,0.3)',
                color: '#E8941A',
                padding: '5px 8px',
                cursor: 'pointer',
                fontFamily: 'monospace', fontSize: '0.55rem',
                letterSpacing: '0.1em',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(232,148,26,0.25)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(232,148,26,0.1)'; }}
            >
              ↩
            </button>
          </div>
        </td>
      </tr>
      {/* Expanded quick-reply panel */}
      {isExpanded && (
        <tr>
          <td colSpan={8} style={{ padding: 0 }}>
            <div style={{
              background: 'rgba(5,10,18,0.8)',
              borderBottom: '2px solid rgba(59,111,212,0.3)',
              borderLeft: '4px solid #3B6FD4',
              padding: '28px 32px'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                {/* Full message */}
                <div>
                  <div style={{
                    fontFamily: 'monospace', fontSize: '0.5rem',
                    letterSpacing: '0.3em', color: '#3B6FD4',
                    textTransform: 'uppercase', marginBottom: '12px'
                  }}>// FULL MELDING</div>
                  <div style={{
                    background: 'rgba(59,111,212,0.06)',
                    border: '1px solid rgba(59,111,212,0.15)',
                    padding: '20px',
                    fontFamily: 'Georgia, serif', fontStyle: 'italic',
                    fontSize: '0.85rem', color: 'rgba(238,240,242,0.8)',
                    lineHeight: 1.7
                  }}>
                    {submission?.message}
                  </div>
                  <div style={{
                    marginTop: '16px',
                    display: 'flex', gap: '16px', flexWrap: 'wrap'
                  }}>
                    <div style={{
                      fontFamily: 'monospace', fontSize: '0.55rem',
                      color: 'rgba(238,240,242,0.35)', letterSpacing: '0.1em'
                    }}>
                      <span style={{ color: 'rgba(238,240,242,0.2)' }}>FRA: </span>
                      <span style={{ color: '#3B6FD4' }}>{submission?.email}</span>
                    </div>
                    {submission?.phone && (
                      <div style={{
                        fontFamily: 'monospace', fontSize: '0.55rem',
                        color: 'rgba(238,240,242,0.35)', letterSpacing: '0.1em'
                      }}>
                        <span style={{ color: 'rgba(238,240,242,0.2)' }}>TLF: </span>
                        <span style={{ color: '#EEF0F2' }}>{submission?.phone}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick reply form */}
                <div>
                  <div style={{
                    fontFamily: 'monospace', fontSize: '0.5rem',
                    letterSpacing: '0.3em', color: '#E8941A',
                    textTransform: 'uppercase', marginBottom: '12px'
                  }}>// HURTIGSVAR</div>

                  {replySent ? (
                    <div style={{
                      background: 'rgba(59,111,212,0.1)',
                      border: '1px solid rgba(59,111,212,0.3)',
                      borderLeft: '3px solid #3B6FD4',
                      padding: '20px',
                      fontFamily: 'monospace', fontSize: '0.7rem',
                      color: '#3B6FD4', letterSpacing: '0.1em'
                    }}>
                      ✓ SVAR SENDT
                    </div>
                  ) : (
                    <>
                      <textarea
                        value={replyText}
                        onChange={e => setReplyText(e?.target?.value)}
                        placeholder="Skriv ditt svar her..."
                        rows={5}
                        style={{
                          width: '100%',
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(59,111,212,0.25)',
                          borderBottom: '2px solid #3B6FD4',
                          color: '#EEF0F2',
                          padding: '14px',
                          fontFamily: 'Georgia, serif',
                          fontSize: '0.82rem',
                          lineHeight: 1.6,
                          outline: 'none',
                          resize: 'vertical',
                          boxSizing: 'border-box',
                          marginBottom: '12px'
                        }}
                      />
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button
                          onClick={handleSendReply}
                          disabled={replyLoading || !replyText?.trim()}
                          style={{
                            background: replyText?.trim() ? '#E8941A' : 'rgba(232,148,26,0.2)',
                            color: replyText?.trim() ? '#050A12' : 'rgba(232,148,26,0.4)',
                            border: 'none',
                            padding: '12px 24px',
                            fontFamily: '"Arial Black", sans-serif',
                            fontSize: '0.65rem', fontWeight: 900,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            cursor: replyText?.trim() ? 'pointer' : 'not-allowed',
                            clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                            transition: 'all 0.2s'
                          }}
                        >
                          {replyLoading ? 'SENDER...' : 'SEND SVAR →'}
                        </button>
                        <div style={{
                          fontFamily: 'monospace', fontSize: '0.5rem',
                          color: 'rgba(238,240,242,0.25)', letterSpacing: '0.1em'
                        }}>Til: {submission?.email}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default SubmissionRow;
